import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'
import { AxiosError } from 'axios'

import { axiosInstance } from '../helpers'
import {
    CreateRenewalSequenceInput,
    RenewalSequence,
    SequenceAiGenerateInput,
    SequenceAiGenerateResponse,
    SequenceMergeVariable,
    UpdateRenewalSequenceInput,
} from '../models/Concession'
import { useUser } from './useUser'

const SEQUENCES_KEY = 'renewal-sequences'
const MERGE_VARS_KEY = 'sequence-merge-variables'
const BASE = '/beta/api/concession/renewal-sequences'

const extractError = (error: unknown, fallback: string): string => {
    const data = (error as AxiosError)?.response?.data as
        | Record<string, unknown>
        | undefined

    if (data) {
        const nonField = data.non_field_errors
        if (Array.isArray(nonField) && nonField.length > 0) {
            return String(nonField[0])
        }
        const firstKey = Object.keys(data)[0]
        if (firstKey) {
            const val = data[firstKey]
            if (Array.isArray(val) && val.length > 0) {
                return `${firstKey}: ${val[0]}`
            }
            if (typeof val === 'string') return val
        }
    }
    return fallback
}

export const useRenewalSequences = () => {
    const queryClient = useQueryClient()
    const { workspaceUser } = useUser()

    const apartmentId = workspaceUser?.active_workspace?.id
    const isApartment =
        workspaceUser?.active_workspace?.company_type === 'APARTMENT'

    const { data: sequences, isLoading, refetch } = useQuery<
        RenewalSequence[],
        Error
    >({
        queryKey: [SEQUENCES_KEY, apartmentId],
        queryFn: async () => {
            const res = await axiosInstance.get<RenewalSequence[]>(`${BASE}/`)
            return res.data
        },
        enabled: isApartment && !!apartmentId,
        refetchOnMount: true,
        refetchOnWindowFocus: false,
    })

    const invalidate = () => {
        queryClient.invalidateQueries({ queryKey: [SEQUENCES_KEY] })
    }

    const createSequence = useMutation({
        mutationFn: async (body: CreateRenewalSequenceInput) => {
            const res = await axiosInstance.post<RenewalSequence>(
                `${BASE}/`,
                body,
            )
            return res.data
        },
        onSuccess: () => {
            invalidate()
            toast.success('Sequence created')
        },
        onError: (err) => {
            toast.error(extractError(err, 'Failed to create sequence'))
        },
    })

    const updateSequence = useMutation({
        mutationFn: async ({
            id,
            body,
        }: {
            id: number
            body: UpdateRenewalSequenceInput
        }) => {
            const res = await axiosInstance.put<RenewalSequence>(
                `${BASE}/${id}/`,
                body,
            )
            return res.data
        },
        onSuccess: () => {
            invalidate()
            toast.success('Sequence updated')
        },
        onError: (err) => {
            toast.error(extractError(err, 'Failed to update sequence'))
        },
    })

    const patchSequence = useMutation({
        mutationFn: async ({
            id,
            body,
        }: {
            id: number
            body: UpdateRenewalSequenceInput
        }) => {
            const res = await axiosInstance.patch<RenewalSequence>(
                `${BASE}/${id}/`,
                body,
            )
            return res.data
        },
        onSuccess: () => {
            invalidate()
            toast.success('Sequence updated')
        },
        onError: (err) => {
            toast.error(extractError(err, 'Failed to update sequence'))
        },
    })

    const deleteSequence = useMutation({
        mutationFn: async (id: number) => {
            await axiosInstance.delete(`${BASE}/${id}/`)
        },
        onSuccess: () => {
            invalidate()
            toast.success('Sequence deleted')
        },
        onError: (err) => {
            toast.error(extractError(err, 'Failed to delete sequence'))
        },
    })

    return {
        sequences: sequences ?? [],
        isLoading,
        refetch,
        createSequence,
        updateSequence,
        patchSequence,
        deleteSequence,
    }
}

export const useSequenceMergeVariables = () => {
    const { workspaceUser } = useUser()
    const isApartment =
        workspaceUser?.active_workspace?.company_type === 'APARTMENT'

    const { data: mergeVariables, isLoading } = useQuery<
        SequenceMergeVariable[],
        Error
    >({
        queryKey: [MERGE_VARS_KEY],
        queryFn: async () => {
            const res = await axiosInstance.get<SequenceMergeVariable[]>(
                `${BASE}/merge-variables/`,
            )
            return res.data
        },
        enabled: isApartment,
        staleTime: 60 * 60 * 1000,
        refetchOnWindowFocus: false,
    })

    return {
        mergeVariables: mergeVariables ?? [],
        isLoading,
    }
}

/** Generate a full sequence draft with Lori AI (nothing is persisted). */
export const generateSequenceAiDraft = async (
    body: SequenceAiGenerateInput,
): Promise<SequenceAiGenerateResponse> => {
    const res = await axiosInstance.post<SequenceAiGenerateResponse>(
        `${BASE}/ai-generate/`,
        body,
    )
    return res.data
}
